import React, { useState } from 'react';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import {
  Box,
  Button,
  IconButton,
  Pagination,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { ROUTE_STRING } from 'constants/RouteString';
import FooterWrapper from '@tenant/core/AppLayout/components/AppFooter/FooterWrapper';
import { PAGE_SIZE } from 'constants/Common';
import AppsHeader from '@tenant/core/AppsContainer/AppsHeader';
import AppSearch from '@tenant/core/AppSearchBar';

import useGetListIDPClient from 'redux/slices/idpclient/hooks/useGetListIDPClient';
import { useSelector } from 'react-redux';
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
} from '@tenant/utility/constants/default';
import useModal from '@tenant/utility/hooks/useModal';
import useDeleteIDPClient from 'redux/slices/idpclient/hooks/useDeleteIDPClient';
import { deepClone } from '@mui/x-data-grid/utils/utils';
import DeleteIDPClientAction from '../Common/DeleteIDPClientAction';
import useNextTick from '@tenant/utility/hooks/useNextTick';
import RefreshIcon from '@mui/icons-material/Refresh';
import AllowedAccess from '@tenant/core/AppPermission/AllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';
import useAllowedAccess from '@tenant/core/AppPermission/useAllowedAccess';
import IdpClientEnum from 'constants/IdpClientEnum';
import AppExpandResult from '@tenant/core/App/AppExpandResult';

import useAddClientByAppInstance from 'redux/slices/idpclient/hooks/useAddClientByAppInstance';
import useUpdateClientByAppInstance from 'redux/slices/idpclient/hooks/useUpdateClientByAppInstance';

import IdpClientModalFormFlat from '../Detail/IdpClients/IdpClientModalFormFlat';
import { getDetailIDPClientQuery } from 'redux/slices/idpclient/apis';

const AppInstanceTable = () => {
  const canEdit = useAllowedAccess([
    AuthorizePermission.IDP_CLIENT_UPDATE,
  ]);
  const canDelete = useAllowedAccess([
    AuthorizePermission.IDP_CLIENT_DELETE,
  ]);

  const nextTick = useNextTick();
  const { filter } = useSelector((s) => s.idpClient);
  const pageSize = filter?.pageSize ?? DEFAULT_PAGE_SIZE;
  const [pageIndex, setPageIndex] = useState(
    filter?.pageIndex ?? DEFAULT_PAGE_INDEX,
  );
  const [searchTerm, setSearchTerm] = useState(filter?.searchTerm ?? '');
  const [isSearchTermChanged, setIsSearchTermChanged] = useState(false);
  const [selected, setSelected] = useState();
  const { visible, onClose, onShow } = useModal();
  const { isLoading, isRefetching, refetch } = useGetListIDPClient({
    pageSize,
    pageIndex,
    searchTerm,
  });
  const loading = isLoading || isRefetching;
  const { list } = useSelector((s) => s.idpClient);
  const totalCount = list?.TotalCount ?? 0;
  const navigate = useNavigate();
  const rows = list?.Items ?? [];
  const deleteMutate = useDeleteIDPClient();

  const clearSearchTerm = async () => {
    setSearchTerm('');
    await nextTick();
    refetch();
  };

  const onChangeSearch = (e) => {
    setSearchTerm(e.target.value);
    setPageIndex(DEFAULT_PAGE_INDEX);
    setIsSearchTermChanged(true);
  };

  const handleCloseDelete = () => {
    setSelected();
    onClose();
  };
  const handleShowDelete = (item) => {
    setSelected(deepClone(item));
    onShow();
  };

  const columns = [
    {
      field: 'AppInstance',
      headerName: 'App Instance',
      width: 175,
      sortable: false,
      flex: 1,
      renderCell: (params) => {
        return params.value?.Name
      },

    },
    {
      field: 'ClientName',
      headerName: 'Client ID',
      width: 175,
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Typography
            variant='body2'
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {params.value}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'AuthenticationFlow',
      headerName: 'Flow',
      width: 175,
      sortable: true,
      renderCell: (params) =>
        IdpClientEnum.find((e) => e.Value === params.value)?.Description,
    },
    {
      field: 'RedirectUris',
      headerName: 'Redirect Uri',
      width: 300,
      sortable: true,
      renderCell: (params) => (
        <AppExpandResult items={params.value} maxVisibleItems={1} />
      ),
    },
    {
      field: 'LogoutRedirectUris',
      headerName: 'Logout Redirect Uri',
      width: 300,
      sortable: true,
      renderCell: (params) => (
        <AppExpandResult items={params.value} maxVisibleItems={1} />
      ),
    },
    {
      field: 'Action',
      headerName: '',
      width: 50,
      type: 'actions',
      align: 'right',
      getActions: (params) => {
        const actions = [];

        if (canEdit) {
          actions.push(
            <GridActionsCellItem
              sx={{ minWidth: 150 }}
              key='app-request-action--edit'
              label='Edit'
              showInMenu
              onClick={() => handleShowEdit(params.row)}
            />
          );
        }

        if (canDelete) {
          actions.push(
            <GridActionsCellItem
              key='app-request-action--delete'
              label='Delete'
              showInMenu
              onClick={() => handleShowDelete(params.row)}
            />
          );
        }

        return actions;
      },
    },
  ]

  const onPageChange = (value) => {
    setPageIndex(value);
  };

  const refreshTableAction = () => {
    if (pageIndex === DEFAULT_PAGE_INDEX) {
      refetch();
      return;
    }

    setPageIndex(DEFAULT_PAGE_INDEX);
  };

  // Add Client Modal
  const {
    visible: visibleAddClient,
    onClose: onCloseAddClient,
    onShow: onShowAddClient,
  } = useModal();
  const addClientMutate = useAddClientByAppInstance();
  const handleSubmitClient = (data) => {
    addClientMutate.mutate(data, {
      onSuccess: () => {
        onCloseAddClient();
      },
    });
  };

  // Edit Client Modal
  const {
    visible: visibleEditClient,
    onClose: onCloseEditClient,
    onShow: onShowEditClient,
  } = useModal();
  const updateClientMutate = useUpdateClientByAppInstance();

  const handleShowEdit = async (item) => {
    try {
      const response = await getDetailIDPClientQuery(item.Id);
      if (response?.data?.Id) {
        setSelected(response.data);
        onShowEditClient();
      }
    } catch (e) {
      console.error(e);
    }
  };
  const handleCloseEdit = () => {
    setSelected();
    onCloseEditClient();
  };
  const handleUpdate = (data) => {
    updateClientMutate.mutate(
      {
        ...data,
        idpClientId: selected.Id,
      },
      {
        onSuccess() {
          handleCloseEdit();
        },
      },
    );
  };

  return (
    <>
      <AppsHeader>
        <Box
          flex={1}
          display='flex'
          justifyContent='space-between'
          alignItems='center'
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <AppSearch
              isClearable
              searchValue={searchTerm}
              onClearSearch={clearSearchTerm}
              iconPosition='right'
              id='outlined-adornment-weight'
              size='small'
              overlap={false}
              onChange={onChangeSearch}
              onBlur={() => {
                isSearchTermChanged && refetch();
                setIsSearchTermChanged(false);
              }}
            ></AppSearch>
          </Box>
          <AllowedAccess
            permissionIds={[AuthorizePermission.IDP_CLIENT_CREATE]}
          >
            <Button variant='outlined' onClick={onShowAddClient}>
              Add New
            </Button>
          </AllowedAccess>
        </Box>
      </AppsHeader>

      <Box style={{ height: 'auto', width: '100%' }}>
        <DeleteIDPClientAction
          open={visible}
          selected={selected}
          loading={deleteMutate?.isPending}
          handleCloseDelete={handleCloseDelete}
          handleSubmitDelete={() => {
            handleCloseDelete();
            refetch();
          }}
        />

        <DataGrid
          hideFooter
          autoHeight
          disableColumnSelector
          disableColumnMenu
          rows={rows}
          columns={columns}
          getRowId={(row) => row.Id}
          loading={loading}
        />
      </Box>

      <FooterWrapper className='footer'>
        <Box justifyContent={'center'} className='footerContainer'>
          <Pagination
            count={Math.ceil(totalCount / PAGE_SIZE) || 1}
            page={pageIndex}
            onChange={(e, v) => onPageChange(v)}
            showFirstButton
            showLastButton
          />
          <Typography>Total: {totalCount || 0}</Typography>
          <Tooltip title='Refresh'>
            <IconButton
              disabled={isRefetching}
              size='small'
              sx={{ ml: 2 }}
              onClick={refreshTableAction}
            >
              <RefreshIcon fontSize='inherit' />
            </IconButton>
          </Tooltip>
        </Box>
      </FooterWrapper>

      <IdpClientModalFormFlat
        visible={visibleAddClient}
        loading={addClientMutate.isPending}
        handleClose={onCloseAddClient}
        handleSubmit={handleSubmitClient}
        isNeedAppInstance={true}
      />

      <IdpClientModalFormFlat
        isEdit
        visible={visibleEditClient}
        loading={updateClientMutate.isPending}
        handleClose={handleCloseEdit}
        handleSubmit={handleUpdate}
        initialValues={
          selected
            ? {
              ...selected,
              RedirectUri: selected.RedirectUris.join('\n'),
              LogoutRedirectUri: selected.LogoutRedirectUris.join('\n'),
              AuthenticationFlow: IdpClientEnum.find(
                (e) => e.Value === selected.AuthenticationFlow,
              ),
              IdpClientId: selected.Id,
              AppInstanceInfo: selected.AppInstance,
              AppInfo: selected.App,
            }
            : undefined
        }
        isNeedAppInstance={true}
      />
    </>
  );
};

export default AppInstanceTable;
